/**
 * S01/N3 (growth-audit 2026-10-06): Shopify's index tokenises SKUs on hyphens ("QG-TM-032-BLK"),
 * so an unpunctuated "QGTM032BLK" / "TM032" matches nothing. Rewrites compact QualGear SKU tokens
 * to the hyphenated form; every other token (words, numbers, already-hyphenated SKUs) is untouched.
 * Families come from the catalog SKUs: TM/PM/DM/AM/SB/DB/MB/PB (QG-TM-032-BLK, QG-TM-A-013,
 * QG-PM-002-S), NE (NE-WC01, QG-NE01-LG) and PRB (PRB-717-WHT). The user's input text is never
 * changed; the rewritten value is used only for the request (predictive fetch / form submit).
 */
window.qgNormalizeSkuQuery = window.qgNormalizeSkuQuery || function (query) {
  if (typeof query !== 'string') return query;
  const mounts = /^(QG)?(TM|PM|DM|AM|SB|DB|MB|PB)([AFT])?(\d{2,4})([A-Z]{1,3})?$/i;
  const ne = /^(QG)?NE([A-Z]{2})?(\d{2})([A-Z]{1,3})?$/i;
  const prb = /^PRB(\d{3})([A-Z]{1,3})?$/i;
  return query.replace(/\S+/g, (token) => {
    let m = token.match(mounts);
    if (m) {
      return [m[1], m[2], m[3], m[4], m[5]].filter(Boolean).join('-').toUpperCase();
    }
    m = token.match(ne);
    if (m) {
      const family = m[2] ? `NE-${m[2]}${m[3]}` : `NE${m[3]}`;
      return [m[1], family, m[4]].filter(Boolean).join('-').toUpperCase();
    }
    m = token.match(prb);
    if (m) {
      return ['PRB', m[1], m[2]].filter(Boolean).join('-').toUpperCase();
    }
    return token;
  });
};

class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.form.addEventListener('submit', this.onSubmitNormalizeSku.bind(this));

      if ((this.input.dataset.secondPlaceholder || this.input.dataset.thirdPlaceholder || this.input.dataset.fourthPlaceholder)
        && (this.input.dataset.animatedPlaceholderMob === 'true' || !window.matchMedia('(max-width: 749.98px)').matches)) {
        this.typewriter();
      }
    }
  }

  shouldResetForm() {
    return !document.querySelector('a[aria-selected="true"], [aria-selected="true"] a'); // RS0930-QCF-D03
  }

  // Submit the hyphenated SKU form while the input keeps showing what the user typed: the input is
  // unnamed for the instant of submission and a hidden q carries the rewritten value.
  onSubmitNormalizeSku() {
    const typed = this.input.value;
    const normalized = window.qgNormalizeSkuQuery(typed);
    if (normalized === typed || !this.input.name) return;
    const name = this.input.name;
    const hidden = document.createElement('input');
    hidden.type = 'hidden';
    hidden.name = name;
    hidden.value = normalized;
    this.input.removeAttribute('name');
    this.input.form.appendChild(hidden);
    setTimeout(() => {
      hidden.remove();
      this.input.setAttribute('name', name);
    }, 0);
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event?.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
    }
  }

  /**
   * Cycles through placeholder strings, simulating typing and deleting.
   */
  async typewriter() {
    const typingSpeed = 40;

    // Collect available placeholder strings
    const placeholders = [
      this.input.placeholder,
      this.input.dataset.secondPlaceholder,
      this.input.dataset.thirdPlaceholder,
      this.input.dataset.fourthPlaceholder,
    ].filter(Boolean);

    if (placeholders.length === 0) return;

    // Small delay helper
    const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const typeText = async (target) => {
      let current = this.input.getAttribute('placeholder') || '';
      // Avoid retyping overlapping prefix
      let remaining = (current.length >= 3 && target.startsWith(current))
        ? target.slice(current.length)
        : target;

      for (const char of remaining) {
        current += char;
        this.input.setAttribute('placeholder', current);
        await wait(typingSpeed);
      }
    };

    const deleteText = async (nextTarget) => {
      let current = this.input.getAttribute('placeholder') || '';

      while (
        current.length > 0 &&
        !(current.length >= 3 && nextTarget.startsWith(current))
        ) {
        current = current.slice(0, -1);
        this.input.setAttribute('placeholder', current);
        await wait(typingSpeed * 0.75);
      }
    };

    // Initial delay before first delete
    await wait(2000);

    let index = 0;
    while (true) {
      const next = placeholders[index = (index + 1) % placeholders.length];
      await deleteText(next);
      await wait(400); // Delay after delete
      await typeText(next);
      await wait(3000); // Delay after type in
    }
  }
}

customElements.define('search-form', SearchForm);
